import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

export class EyeBloom {
  private readonly eyeTarget = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType });
  private readonly bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.55, 0.2, 0);
  private readonly black = new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.DoubleSide });
  private readonly emitters = new Map<THREE.MeshStandardMaterial, THREE.MeshBasicMaterial>();
  private readonly size = new THREE.Vector2();
  private readonly renderedSize = new THREE.Vector2();
  private readonly composite = new THREE.ShaderMaterial({
    uniforms: {
      bloomTexture: { value: this.bloom.renderTargetsHorizontal[0].texture },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D bloomTexture;
      varying vec2 vUv;
      void main() {
        vec3 glow = max(texture2D(bloomTexture, vUv).rgb - vec3(0.002), vec3(0.0));
        float alpha = clamp(max(glow.r, max(glow.g, glow.b)), 0.0, 1.0);
        gl_FragColor = vec4(glow, alpha);
        #include <colorspace_fragment>
        gl_FragColor *= 0.45;
      }
    `,
    depthTest: false,
    depthWrite: false,
    transparent: true,
    toneMapped: false,
    blending: THREE.CustomBlending,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneFactor,
    blendEquation: THREE.AddEquation,
    blendSrcAlpha: THREE.OneFactor,
    blendDstAlpha: THREE.OneFactor,
    blendEquationAlpha: THREE.MaxEquation,
  });
  private readonly quad = new FullScreenQuad(this.composite);

  constructor(private readonly renderer: THREE.WebGLRenderer) {
    // Coarse mips would spread a tiny LED across the entire thumbnail.
    this.bloom.bloomTintColors.slice(2).forEach((color) => color.setScalar(0));
  }

  render(
    scene: THREE.Scene,
    camera: THREE.Camera,
    eyeMaterials: readonly THREE.MeshStandardMaterial[],
    target: THREE.WebGLRenderTarget | null = null,
  ) {
    const renderer = this.renderer;
    const previousTarget = renderer.getRenderTarget();
    const autoClear = renderer.autoClear;
    try {
      renderer.setRenderTarget(target);
      renderer.render(scene, camera);
      if (!eyeMaterials.some((material) => material.emissiveIntensity > 0 && material.emissive.getHex() !== 0)) {
        return;
      }

      if (target) this.size.set(target.width, target.height);
      else renderer.getDrawingBufferSize(this.size);
      if (!this.renderedSize.equals(this.size)) {
        this.renderedSize.copy(this.size);
        const width = Math.max(1, Math.round(this.size.x / 2));
        const height = Math.max(1, Math.round(this.size.y / 2));
        this.eyeTarget.setSize(width, height);
        this.bloom.setSize(width, height);
      }
      const maskMaterials = new Map<THREE.Material, THREE.Material>();
      eyeMaterials.forEach((material) => {
        let emitter = this.emitters.get(material);
        if (!emitter) {
          emitter = new THREE.MeshBasicMaterial({ side: material.side });
          this.emitters.set(material, emitter);
        }
        emitter.color.copy(material.emissive).multiplyScalar(material.emissiveIntensity);
        maskMaterials.set(material, emitter);
      });
      const originals = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
      const hidden: THREE.Object3D[] = [];
      const background = scene.background;
      const clearColor = renderer.getClearColor(new THREE.Color());
      const clearAlpha = renderer.getClearAlpha();
      const shadowsEnabled = renderer.shadowMap.enabled;
      try {
        // Black occluders keep eyes hidden behind the head/body, including physics clones.
        scene.traverse((object) => {
          if (object instanceof THREE.Mesh) {
            originals.set(object, object.material);
            object.material = Array.isArray(object.material)
              ? object.material.map((material) => maskMaterials.get(material) ?? this.black)
              : maskMaterials.get(object.material) ?? this.black;
          } else if (object.visible && (
            object instanceof THREE.Line || object instanceof THREE.Points || object instanceof THREE.Sprite
          )) {
            object.visible = false;
            hidden.push(object);
          }
        });
        scene.background = null;
        renderer.setClearColor(0x000000, 0);
        renderer.shadowMap.enabled = false;
        renderer.setRenderTarget(this.eyeTarget);
        renderer.render(scene, camera);
        this.bloom.render(renderer, this.eyeTarget, this.eyeTarget, 0, false);
      } finally {
        originals.forEach((material, mesh) => { mesh.material = material; });
        hidden.forEach((object) => { object.visible = true; });
        scene.background = background;
        renderer.setClearColor(clearColor, clearAlpha);
        renderer.shadowMap.enabled = shadowsEnabled;
      }

      renderer.setRenderTarget(target);
      renderer.autoClear = false;
      this.quad.render(renderer);
    } finally {
      renderer.autoClear = autoClear;
      renderer.setRenderTarget(previousTarget);
    }
  }

  dispose() {
    this.eyeTarget.dispose();
    this.bloom.dispose();
    this.black.dispose();
    this.emitters.forEach((material) => material.dispose());
    this.emitters.clear();
    this.composite.dispose();
    this.quad.dispose();
  }
}
